import { useState } from 'react';
import { hexToRgba, fmtDate, addDays, startOfWeek, DAY_CELL_HEIGHT, DAY_TIME_SLOTS, DAY_SLOT_MINUTES, WEEKDAYS_ES, WEEKDAYS_EN, timeToMinutesFrom8 } from './agendaConstants';
import { ApptChip } from './ApptChip';
import { ScheduleSlotCell } from './ScheduleSlotHover';
import { useHolidays } from './useHolidays';

export function WeekView({ date, appointments, studies, onClickAppt, onClickSlot, onClickProgrammedSlot, lang, onDragStart, onDrop, scheduleSlots, slotLocks = [], scheduleBlocks = [], onContextMenuSlot, onClickBlock }) {
  const weekStart = startOfWeek(date);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const studyMap = {};
  studies.forEach(s => { studyMap[s.study_id] = s; });
  const weekdays = lang === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;
  const today = fmtDate(new Date());
  const holidays = useHolidays();
  const [dragOver, setDragOver] = useState(null);
  // URS-F196: indice rapido de locks
  const lockByKey = {};
  slotLocks.forEach(lk => {
    lockByKey[`${lk.date}|${lk.study_id}|${lk.visit_code}|${lk.start_time}`] = lk;
  });

  // URS-F188: 5-minute cadence in WeekView (parity with DayView). Tolerance
  // +/-5 min keeps multi-slot reservations highlighted on every 5-min row.
  const getSlotsForCell = (dayStr, slotLabel) => {
    return (scheduleSlots || []).filter(s => s.date === dayStr && s.start_time <= slotLabel && s.end_time > slotLabel);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
      <div className="flex border-b border-stone-200 bg-stone-50">
        <div className="w-14 shrink-0 p-2"></div>
        {days.map((d, i) => {
          const ds = fmtDate(d);
          return (
            <div key={i} className={`flex-1 p-2 text-center border-l border-stone-200 ${holidays[ds] ? 'bg-amber-50' : ds === today ? 'bg-[#1f3e80]/5' : ''}`} data-testid={holidays[ds] ? `week-holiday-${ds}` : undefined}>
              <p className="text-xs text-stone-500">{weekdays[i]}</p>
              <p className={`text-sm font-semibold ${ds === today ? 'text-[#1f3e80]' : 'text-stone-800'}`}>{d.getDate()}</p>
              {holidays[ds] && <p className="text-[10px] font-medium text-amber-700 truncate" title={holidays[ds]}>{holidays[ds]}</p>}
            </div>
          );
        })}
      </div>
      <div className="flex max-h-[600px] overflow-y-auto">
        <div className="w-14 shrink-0 border-r border-stone-100">
          {DAY_TIME_SLOTS.map(slot => {
            const isHour = slot.m === 0;
            const isQuarter = slot.m === 15 || slot.m === 30 || slot.m === 45;
            const borderClass = isHour
              ? 'border-b border-stone-200'
              : (isQuarter ? 'border-b border-stone-100' : 'border-b border-stone-50');
            return (
              <div key={slot.label} style={{ height: DAY_CELL_HEIGHT, lineHeight: `${DAY_CELL_HEIGHT}px` }}
                className={`px-1 flex items-center justify-end ${borderClass} ${isHour ? 'text-[11px] font-mono font-semibold text-stone-600' : (isQuarter ? 'text-[9px] font-mono text-stone-400' : 'text-[8px] font-mono text-stone-300')}`}>
                <span style={{ lineHeight: 1 }}>{slot.label}</span>
              </div>
            );
          })}
        </div>
        {days.map((d, dayIdx) => {
          const ds = fmtDate(d);
          const dayAppts = appointments.filter(a => a.date === ds);
          const apptsByTime = {};
          dayAppts.forEach(a => { const k = a.time || '08:00'; if (!apptsByTime[k]) apptsByTime[k] = []; apptsByTime[k].push(a); });
          // Precompute column assignment per time bucket so distintas citas
          // del mismo estudio a la misma hora (VAL + T0) ocupen columnas
          // separadas en lugar de superponerse. Prioridad: (study_id+visit_code)
          // -> study_id -> nueva columna.
          const positionByAppt = {};
          Object.keys(apptsByTime).forEach(timeKey => {
            const tg = apptsByTime[timeKey];
            const cs = getSlotsForCell(ds, timeKey);
            const csUsed = new Array(cs.length).fill(false);
            const assigned = [];
            const pending = [];
            tg.forEach(appt => {
              const idx = cs.findIndex((c, i) => !csUsed[i] && c.study_id === appt.study_id && c.visit_code === appt.visit_code);
              if (idx >= 0) { csUsed[idx] = true; assigned.push({ appt, col: idx }); }
              else pending.push(appt);
            });
            const stillPending = [];
            pending.forEach(appt => {
              const idx = cs.findIndex((c, i) => !csUsed[i] && c.study_id === appt.study_id);
              if (idx >= 0) { csUsed[idx] = true; assigned.push({ appt, col: idx }); }
              else stillPending.push(appt);
            });
            stillPending.forEach((appt, i) => assigned.push({ appt, col: cs.length + i }));
            const totalCols = Math.max(1, cs.length + stillPending.length);
            const widthPct = 100 / totalCols;
            assigned.forEach(({ appt, col }) => {
              positionByAppt[appt.appointment_id] = { leftPct: col * widthPct, widthPct };
            });
          });
          const getApptPosition = (appt) => positionByAppt[appt.appointment_id] || { leftPct: 0, widthPct: 100 };
          return (
            <div key={dayIdx} className={`flex-1 border-l border-stone-200 relative ${holidays[ds] ? 'bg-[repeating-linear-gradient(135deg,rgba(217,119,6,0.07)_0px,rgba(217,119,6,0.07)_6px,transparent_6px,transparent_12px)]' : ds === today ? 'bg-[#1f3e80]/[0.02]' : ''}`}>
              {DAY_TIME_SLOTS.map(slot => {
                const cellKey = `${ds}|${slot.label}`;
                const isDragTarget = dragOver === cellKey;
                const cellSchedule = getSlotsForCell(ds, slot.label);
                const isHour = slot.m === 0;
                const isQuarter = slot.m === 15 || slot.m === 30 || slot.m === 45;
                const borderClass = isHour ? 'border-b border-stone-200' : (isQuarter ? 'border-b border-stone-100' : 'border-b border-stone-50');
                return (
                  <div key={slot.label} style={{ height: DAY_CELL_HEIGHT }}
                    className={`cursor-pointer relative ${borderClass} ${isDragTarget ? 'bg-[#1f3e80]/10' : 'hover:bg-stone-50/50'} transition-colors`}
                    onClick={() => onClickSlot(ds, slot.label)}
                    onContextMenu={(e) => {
                      if (!onContextMenuSlot) return;
                      e.preventDefault();
                      onContextMenuSlot(ds, slot.label, e.clientX, e.clientY);
                    }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(cellKey); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(null); onDrop(ds, slot.label); }}>
                    {cellSchedule.length > 0 && (
                      <div className="absolute inset-0 flex">
                        {cellSchedule.map((cs, ci) => {
                          const study = studyMap[cs.study_id];
                          const color = study?.color || '#888';
                          const lockKey = `${ds}|${cs.study_id}|${cs.visit_code}|${cs.start_time}`;
                          const lock = lockByKey[lockKey];
                          const handleClick = (e) => {
                            e.stopPropagation();
                            if (lock) return;
                            if (onClickProgrammedSlot) {
                              onClickProgrammedSlot({
                                study_id: cs.study_id,
                                visit_code: cs.visit_code,
                                date: ds,
                                start_time: cs.start_time,
                                end_time: cs.end_time,
                              });
                            } else {
                              onClickSlot(ds, slot.label);
                            }
                          };
                          return (
                            <ScheduleSlotCell
                              key={ci}
                              slot={cs}
                              study={study}
                              lock={lock}
                              isHour={isHour}
                              onClick={handleClick}
                              dayStr={ds}
                              appointments={appointments}
                              lang={lang}
                              borderWidth={2}
                              alwaysShowLabel={true}
                              testId={`schedule-cell-${cs.study_id}-${cs.visit_code}-${slot.label}`}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {(() => {
                // URS-F252: bloqueos del dia (globales + por estudio).
                const dayBlocks = (scheduleBlocks || []).filter(b => b.date === ds);
                if (!dayBlocks.length) return null;
                return (
                  <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 8 }}>
                    {dayBlocks.map(b => {
                      const startMin = timeToMinutesFrom8(b.start_time);
                      const endMin = timeToMinutesFrom8(b.end_time);
                      const top = (startMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                      const height = Math.max(0, ((endMin - startMin) / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT);
                      if (height <= 0) return null;
                      const isGlobal = !b.study_id;
                      const study = !isGlobal ? studyMap[b.study_id] : null;
                      const bg = isGlobal
                        ? 'repeating-linear-gradient(45deg, rgba(220,38,38,0.28) 0px, rgba(220,38,38,0.28) 6px, rgba(220,38,38,0.12) 6px, rgba(220,38,38,0.12) 12px)'
                        : 'repeating-linear-gradient(45deg, rgba(220,38,38,0.18) 0px, rgba(220,38,38,0.18) 5px, rgba(220,38,38,0.06) 5px, rgba(220,38,38,0.06) 10px)';
                      return (
                        <div
                          key={b.block_id}
                          data-testid={`schedule-block-${b.block_id}`}
                          onClick={(e) => { e.stopPropagation(); if (onClickBlock) onClickBlock(b); }}
                          title={`${isGlobal ? (lang === 'es' ? 'Bloqueo global' : 'Global block') : (study?.code || b.study_id)} · ${b.start_time}-${b.end_time} · ${b.reason || ''}`}
                          style={{
                            position: 'absolute', top, height, left: 0, right: 0,
                            background: bg,
                            borderLeft: isGlobal ? '2px solid #dc2626' : '2px solid rgba(220,38,38,0.5)',
                            pointerEvents: 'auto', cursor: onClickBlock ? 'pointer' : 'default',
                          }}
                        >
                          <div className="px-1 py-0.5 text-[9px] font-medium text-red-800 truncate select-none">
                            {isGlobal ? '🔒' : (study?.code || '')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 10 }}>
                {dayAppts.map(appt => {
                  const offsetMin = timeToMinutesFrom8(appt.time);
                  const duration = appt.duration_minutes || 15;
                  const spanCells = Math.max(1, Math.round(duration / DAY_SLOT_MINUTES));
                  const top = (offsetMin / DAY_SLOT_MINUTES) * DAY_CELL_HEIGHT;
                  const height = spanCells * DAY_CELL_HEIGHT;
                  const { leftPct, widthPct } = getApptPosition(appt);
                  const study = studyMap[appt.study_id];
                  return (
                    <div key={appt.appointment_id} style={{ position: 'absolute', top, height, left: `${leftPct}%`, width: `${widthPct}%`, pointerEvents: 'auto', padding: '0 1px' }}>
                      <ApptChip appt={appt} study={study} onClick={onClickAppt} compact onDragStart={onDragStart} spanCells={spanCells} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
